import { todayPT } from "@homebound/activesupport";
import { Css, PlainFormSectionChild } from "@homebound/beam";
import { ObjectConfig, ObjectState } from "@homebound/form-state";
import { ReactNode } from "react";
import {
  Maybe,
  ReadyPlanOptionGroupPersonalizationType as PersonalizationType,
  CheckoutConfigOptionGroupStep as Step,
} from "src/generated/graphqlTypes";
import { ExteriorOptionCard } from "src/routes/projects/change-events/guided/workflows/components/ExteriorOptionCard";
import {
  cutoffTag,
  isExteriorGroup,
  type OptionTags,
} from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { packageOptionCard } from "src/routes/projects/change-events/guided/workflows/components/PackageOptionCard";
import { personalizationOptionCard } from "src/routes/projects/change-events/guided/workflows/components/PersonalizationOptionCard";
import { ProductOptionCard } from "src/routes/projects/change-events/guided/workflows/components/ProductOptionCard";
import { SingleOrLinkedOptionCard } from "src/routes/projects/change-events/guided/workflows/components/SingleOrLinkedOptionCard";
import {
  SelectOptionsUtils_CheckoutConfigFragment,
  SelectOptionsUtils_OptionGroupFragment,
} from "./selectOptionsUtils.generated";

/** What the user picked in one option group's card. */
export type SelectOptionsFormInput = {
  /** The `ReadyPlanOptionGroup` id, which is what `parentGroup` / `childGroup` point at. */
  groupId: string;
  /** Whether the group's checkbox is on. Cards without one (radio lists) are always `true`. */
  selected: boolean | null | undefined;
  /** The `ReadyPlanOption` picked in the group; only saved while `selected`. */
  optionId: string | null | undefined;
};

/**
 * Whether each of the group's options is one product, i.e. flooring planks or pendants: single/linked products, or a
 * package shown as a single product. These get the card with a product thumbnail per option.
 */
export function isProductOptionGroup(group: SelectOptionsUtils_OptionGroupFragment): boolean {
  const { personalizationType, displayPackageOptionsAsSingleProduct } = group.group;
  const code = personalizationType?.code;
  return (
    code === PersonalizationType.SingleProduct ||
    code === PersonalizationType.LinkedProduct ||
    (code === PersonalizationType.Package && !!displayPackageOptionsAsSingleProduct)
  );
}

/**
 * Whether the group's card has a checkbox, which like checkout's `ProductAddOnCard` is only single-option groups
 * (structural, add-ons). Everything else (Elevation, schemes, Spec Level, packages, pick-one product groups like a
 * chandelier, personalization child variants, whose on/off is their parent's) always has a pick, so it has no on/off.
 */
export function hasToggle(group: SelectOptionsUtils_OptionGroupFragment): boolean {
  return !group.parentGroup && group.group.isSingleOptionGroup;
}

/**
 * The step's sections in design order, straight from the backend's `step` / `subStepNumber` (groups already arrive in
 * checkout order). Groups with a `parentGroup` are rendered inside their parent, so they never get a section of their
 * own, and empty sections are dropped.
 *
 * The interior design package groups (Interior Scheme, Spec Level) land on the first sub-step alongside the
 * personalization ones, so the sub-steps are bucketed by number rather than by step.
 *
 * ADU: every group on the ADU product offering carries `step: ADU` and no sub-step (the backend overwrites the computed
 * step for ADU plans), so the step-based mapping above yields nothing for it. Like checkout's ADU summary card, its
 * sections come from the group's global option type instead: Elevation, Exterior Scheme, Interior Scheme, Spec Level
 * and ADU Selections (everything else).
 */
export function toSelectOptionsSections(
  optionGroups: SelectOptionsUtils_OptionGroupFragment[],
  interiorSubSteps: SelectOptionsUtils_CheckoutConfigFragment["interiorSubSteps"],
) {
  const groups = optionGroups.filter((group) => !group.parentGroup);
  const inSteps = (steps: Step[]) => groups.filter((group) => steps.includes(group.step));
  const bySubStep = inSteps([Step.InteriorDesignPackage, Step.InteriorPersonalization])
    .filter((group) => !!group.subStepNumber)
    .groupBy((group) => group.subStepNumber!);
  const aduByCategory = inSteps([Step.Adu]).groupBy(toAduCategory);
  const sections = [
    { title: "Exterior", groups: inSteps([Step.ExteriorElevation, Step.ExteriorDesignPackage]) },
    { title: "Structural", groups: inSteps([Step.OptionsStructural]) },
    ...interiorSubSteps.map((subStep) => ({ title: subStep.displayName, groups: bySubStep[subStep.stepNumber] ?? [] })),
    { title: "Add-Ons", groups: inSteps([Step.OptionsAddon]) },
    // The ADU side's sections; a main house side has no `step: ADU` groups, so these are empty and dropped there
    ...aduCategories.map((category) => ({ title: category, groups: aduByCategory[category] ?? [] })),
  ];
  return sections.filter((section) => section.groups.nonEmpty);
}

/** Checkout's `aduGroupSections` labels, in its order. */
const aduCategories = ["Elevation", "Exterior Scheme", "Interior Scheme", "Spec Level", "ADU Selections"] as const;
type AduCategory = (typeof aduCategories)[number];

/** Checkout's `getAduGroupCategory`: which ADU section a group belongs to, from its global option type flags. */
function toAduCategory(group: SelectOptionsUtils_OptionGroupFragment): AduCategory {
  const { isElevation, isExteriorScheme, isInteriorScheme, isSpecLevel } = group.group.group.type;
  if (isElevation) return "Elevation";
  if (isExteriorScheme) return "Exterior Scheme";
  if (isInteriorScheme) return "Interior Scheme";
  if (isSpecLevel) return "Spec Level";
  return "ADU Selections";
}

/**
 * Each group with its options in checkout's order: selected first, then `checkoutOrder`, price ascending, then name.
 *
 * Pass a `previous` result to keep its order (like checkout, so a new pick doesn't jump to the top); options it
 * doesn't have go after it.
 */
export function sortGroupOptions(
  groups: SelectOptionsUtils_OptionGroupFragment[],
  previous: SelectOptionsUtils_OptionGroupFragment[] = [],
): SelectOptionsUtils_OptionGroupFragment[] {
  const previousIndex = new Map(previous.flatMap((group) => group.options.map((o, i) => [o.option.id, i] as const)));
  return groups.map((group) => ({
    ...group,
    options: group.options.sortBy((o) => [
      previousIndex.get(o.option.id) ?? Number.MAX_SAFE_INTEGER,
      o.isSelected ? 0 : 1,
      o.option.checkoutOrder ?? 0,
      o.totalPriceInCents,
      o.option.name,
    ]),
  }));
}

/**
 * Whether the group is shown on the page:
 * - hidden when the server says it can't be selected (e.g. its prerequisite options aren't picked);
 * - otherwise shown unless it's expired, and an expired group still shows when the user toggled "show expired" or
 *   when it has a selected option (so the user can see and undo it).
 */
export function showGroup(
  group: SelectOptionsUtils_OptionGroupFragment,
  row: SelectOptionsFormInput | undefined,
  showExpired: boolean,
): boolean {
  if (!group.canBeSelected.allowed) return false;
  const isExpired = !!group.cutoffDate && group.cutoffDate.isBefore(todayPT());
  if (showExpired || !isExpired) return true;
  return row ? !!row.selected && !!row.optionId : group.options.some((option) => option.isSelected);
}

/**
 * Given an option group, creates a plain section to be used in a `FormSectionLayout`, with the card its group type
 * calls for (exterior, personalization, product, package, etc.) bound to the group's row in the rows form.
 * `allGroups` is used to find the group's `childGroup`, whose row drives the variant pick.
 */
export function createOptionGroupSection(
  group: SelectOptionsUtils_OptionGroupFragment,
  allGroups: SelectOptionsUtils_OptionGroupFragment[],
  rowFor: (rpogId: string) => ObjectState<SelectOptionsFormInput>,
  priceMode: OptionPriceMode,
  opts: Partial<{
    /** Tags keyed by `ReadyPlanOption` id, shown next to the matching options on the card. */
    optionTags: OptionTags;
    /** Rendered below the card. Wrap it in an `Observer` if it reads the rows form. */
    footer: ReactNode;
    /** Whether a product group's card shows a checkbox. Defaults to `hasToggle`. */
    toggle: boolean;
  }> = {},
): PlainFormSectionChild {
  const { footer, toggle = hasToggle(group) } = opts;
  const section = createOptionGroupCard(group, allGroups, rowFor, priceMode, opts.optionTags, toggle);
  if (!footer) return section;
  return {
    ...section,
    fields: (
      <div css={Css.df.fdc.gap2.$}>
        {section.fields}
        {footer}
      </div>
    ),
  };
}

/** The section for the group's card, which `createOptionGroupSection` then decorates. */
function createOptionGroupCard(
  group: SelectOptionsUtils_OptionGroupFragment,
  allGroups: SelectOptionsUtils_OptionGroupFragment[],
  rowFor: (rpogId: string) => ObjectState<SelectOptionsFormInput>,
  priceMode: OptionPriceMode,
  optionTags: OptionTags | undefined,
  toggle: boolean,
): PlainFormSectionChild {
  const row = rowFor(group.group.id);
  const base = { id: group.id, title: group.group.name, tag: cutoffTag(group.cutoffDate) };
  // Product cards show the cutoff tag next to their price, so their section header skips it rather than repeat it
  const productBase = { ...base, tag: undefined };

  const childGroup = group.childGroup && allGroups.find((g) => g.group.id === group.childGroup!.id);
  if (isExteriorGroup(group)) {
    // Elevations and exterior schemes, the main house's and the ADU's: image cards, pick one
    return {
      ...base,
      fields: <ExteriorOptionCard group={group} field={row.optionId} priceMode={priceMode} optionTags={optionTags} />,
    };
  } else if (childGroup && isProductOptionGroup(childGroup)) {
    // "Add Decorative Openings" with a product per variant: a checkbox for the add-on plus the variants' thumbnails
    return {
      ...productBase,
      fields: (
        <SingleOrLinkedOptionCard
          group={group}
          childGroup={childGroup}
          selectedField={row.selected}
          optionField={rowFor(childGroup.group.id).optionId}
          priceMode={priceMode}
          optionTags={optionTags}
        />
      ),
    };
  } else if (childGroup) {
    // "Add Washer and Dryer" with package variants: a checkbox for the add-on plus a radio row per variant
    return personalizationOptionCard({
      group,
      childGroup,
      selectedField: row.selected,
      variantField: rowFor(childGroup.group.id).optionId,
      priceMode,
      optionTags,
    });
  } else if (isProductOptionGroup(group)) {
    // Flooring, pendants: a thumbnail per product. A pick-one group like a chandelier has no checkbox
    return {
      ...productBase,
      fields: (
        <SingleOrLinkedOptionCard
          group={group}
          selectedField={toggle ? row.selected : undefined}
          optionField={row.optionId}
          priceMode={priceMode}
          optionTags={optionTags}
        />
      ),
    };
  } else if (group.group.isSingleOptionGroup) {
    // Structural options and add-ons, e.g. "Holiday Lighting": one product with a checkbox
    return {
      ...productBase,
      fields: <ProductOptionCard group={group} field={row.selected} priceMode={priceMode} optionTags={optionTags} />,
      // No title for single-option groups otherwise it'd duplicate the card's content.
      title: "",
    };
  } else {
    // Interior schemes, Spec Level, appliance packages: a radio row per package, pick one
    return packageOptionCard({ group, field: row.optionId, priceMode, optionTags });
  }
}

export type SelectOptionsRowsInput = { groups: SelectOptionsUtils_OptionGroupFragment[]; optionIds: Maybe<string[]> };

/**
 * Builds one form row per group (child groups included) from the config's current `optionIds`:
 * - `selected` is whether the group currently has a pick; groups without a checkbox are always `true`;
 * - `optionId` is the current pick, or for toggle groups the first option (the card shows it even while off).
 */
export function selectOptionsRowsMapToForm(input: SelectOptionsRowsInput): { rows: SelectOptionsFormInput[] } {
  const { groups, optionIds = [] } = input;
  const optionIdSet = new Set(optionIds);
  const rows = groups.map((ccfgog) => {
    const currentRpoId = ccfgog.options.find((ccfgpo) => optionIdSet.has(ccfgpo.option.id))?.option.id;
    const toggle = hasToggle(ccfgog);
    return {
      groupId: ccfgog.group.id,
      selected: toggle ? currentRpoId !== undefined : true,
      optionId: currentRpoId ?? (toggle ? ccfgog.options.first?.option.id : undefined),
    };
  });
  return { rows };
}

export const selectOptionsRowsFormConfig: ObjectConfig<{ rows: SelectOptionsFormInput[] }> = {
  rows: {
    type: "list",
    // `groupId` is the row's identity, so a refresh updates each row in place and the cards' bound fields stay live
    config: { groupId: { type: "value", isIdKey: true }, selected: { type: "value" }, optionId: { type: "value" } },
  },
};
